import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, CheckCheck, Info, CheckCircle2, AlertCircle, AlertTriangle, Loader2 } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { cn } from '@/utils/cn'
import { formatRelative } from '@/utils/format-date'
import type { AppNotification } from '@/types/notification'

interface NotificationsModalProps {
  open: boolean
  onClose: () => void
  items: AppNotification[]
  loading: boolean
  unreadCount: number
  onLoad: () => void
  onMarkAsRead: (id: string) => Promise<void>
  onMarkAllAsRead: () => Promise<void>
}

function TypeIcon({ type }: { type: string }) {
  const cls = 'shrink-0'
  switch (type) {
    case 'success':
      return <CheckCircle2 size={18} className={cn(cls, 'text-emerald-500')} />
    case 'error':
      return <AlertCircle size={18} className={cn(cls, 'text-red-500')} />
    case 'warning':
      return <AlertTriangle size={18} className={cn(cls, 'text-amber-500')} />
    default:
      return <Info size={18} className={cn(cls, 'text-[var(--brand-primary)]')} />
  }
}

export function NotificationsModal({
  open,
  onClose,
  items,
  loading,
  unreadCount,
  onLoad,
  onMarkAsRead,
  onMarkAllAsRead,
}: NotificationsModalProps) {
  const navigate = useNavigate()

  useEffect(() => {
    if (open) onLoad()
  }, [open, onLoad])

  const handleItemClick = async (notification: AppNotification) => {
    if (!notification.lida) {
      try {
        await onMarkAsRead(notification.id)
      } catch {
        /* ignore */
      }
    }
    if (notification.link) {
      onClose()
      navigate(notification.link)
    }
  }

  const hasUnread = unreadCount > 0

  return (
    <Modal
      open={open}
      onClose={onClose}
      title='Notificações'
      description={
        hasUnread
          ? `${unreadCount} não ${unreadCount === 1 ? 'lida' : 'lidas'}`
          : 'Tudo em dia'
      }
      size='lg'
      className='max-h-[min(520px,85vh)]'
      footer={
        hasUnread ? (
          <Button
            variant='secondary'
            size='sm'
            icon={<CheckCheck size={14} />}
            onClick={() => void onMarkAllAsRead()}
          >
            Marcar todas como lidas
          </Button>
        ) : undefined
      }
    >
      {loading ? (
        <div className='flex flex-col items-center justify-center gap-2 py-12 text-text-muted'>
          <Loader2 size={24} className='animate-spin' />
          <p className='text-sm'>Carregando…</p>
        </div>
      ) : items.length === 0 ? (
        <div className='flex flex-col items-center justify-center gap-3 py-12 text-center'>
          <div className='flex h-12 w-12 items-center justify-center rounded-full bg-[var(--glass-bg)]'>
            <Bell size={22} className='text-text-muted' strokeWidth={1.5} />
          </div>
          <p className='text-sm font-medium text-text-primary'>Nenhuma notificação</p>
          <p className='max-w-xs text-xs text-text-muted'>
            Alertas de deals, handoffs e outras ações do CRM aparecem aqui.
          </p>
        </div>
      ) : (
        <ul className='-mx-1 max-h-[min(360px,50vh)] space-y-1 overflow-y-auto pr-1'>
          {items.map((n) => (
            <li key={n.id}>
              <button
                type='button'
                onClick={() => void handleItemClick(n)}
                className={cn(
                  'flex w-full gap-3 rounded-xl px-3 py-3 text-left transition-colors',
                  n.lida
                    ? 'hover:bg-[var(--glass-bg)]'
                    : 'bg-[var(--brand-primary-muted)]/40 hover:bg-[var(--brand-primary-muted)]/60',
                  n.link && 'cursor-pointer',
                )}
              >
                <TypeIcon type={n.type} />
                <div className='min-w-0 flex-1'>
                  <div className='flex items-start justify-between gap-2'>
                    <p
                      className={cn(
                        'text-sm leading-snug',
                        n.lida ? 'font-medium text-text-secondary' : 'font-semibold text-text-primary',
                      )}
                    >
                      {n.title}
                    </p>
                    {!n.lida && (
                      <span
                        className='mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[var(--brand-primary)]'
                        aria-hidden
                      />
                    )}
                  </div>
                  <p className='mt-0.5 line-clamp-2 text-xs text-text-muted'>{n.message}</p>
                  <p className='mt-1 text-[11px] text-text-muted'>{formatRelative(n.createdAt)}</p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}
